/* eslint-disable @typescript-eslint/naming-convention */
// deps
import { useContext, useCallback, useMemo } from "react";

// internal-deps
import {
    RenderingDetails,
    ValueDrivenCrossSellXP,
    SavingsLineItemData,
    GiftingCartApiResponse,
} from "@swiggy-private/cart-types";
import { VALID_RENDERING_DETAILS_TYPE } from "@swiggy-private/cart-ui";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/js-utils";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";

// hooks
import { useCartRendererXP } from "./useCartRendererXP";
import { useDirectCouponSavingsData } from "./useDirectCouponSavingsData";
import { useAppliedCouponSavingsData } from "./useAppliedCouponSavingsData";
import { useCouponlessSavingsData } from "./useCouponlessSavingsData";
import { useCouponButtonSavingsData } from "./useCouponButtonSavingsData";
import { useIMCouponlessSavingsData } from "./useIMCouponlessSavingsData";

// helpers
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";
import { getCartId, isPORV2Flow } from "@cart-client/helpers/Cart";
import {
    getApplyPaymentCouponWidgetData,
    getApplyCouponWidgetData,
    isDirectCouponUsecase,
    isDirectPaymentCouponUsecase,
    buildGiftingExtraParamsForSavingsCornerAnalytics,
    buildGiftingCouponCodeList,
} from "@cart-client/helpers/Coupon";
import { getSavingsCornerClickStreamData } from "@cart-client/helpers/SavingsCorner";

// analytics
import { impressionSavingsCornerLineItem } from "@cart-client/analytics/events";

// constants
import { CouponType } from "@cart-client/constants/Coupon";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

export interface IProps {
    couponSavingsRenderingDetails: RenderingDetails[];
}

export interface UseCouponSavingsReturnType {
    getCouponSavingsLineItems: () => SavingsLineItemData[];
}

export const useCouponSavings = ({ couponSavingsRenderingDetails }: IProps): UseCouponSavingsReturnType => {
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;
    const { data } = useContext(CartFlowContext);
    const { impressionEvent } = useContext(AnalyticsContext);

    const { xpSelectiveCouponVariant, valueDrivenCrossSellXPVariant, porFlowXP } = useCartRendererXP(cartResponse);
    const applyCouponWidgetData = useMemo(() => getApplyCouponWidgetData(cartResponse), [cartResponse]);
    const applyPaymentCouponWidgetData = useMemo(() => getApplyPaymentCouponWidgetData(cartResponse), [cartResponse]);

    const isNewPORFlow = isPORV2Flow(porFlowXP);

    const cartId = !isEmpty(cartResponse) ? getCartId(cartResponse) : "";

    const [analyticsExtraParams, analyticsContext] = useMemo(() => {
        if (isGiftingCartSource(data?.cartSource)) {
            return [
                buildGiftingExtraParamsForSavingsCornerAnalytics(cartResponse?.data as GiftingCartApiResponse),
                buildGiftingCouponCodeList(cartResponse?.data as GiftingCartApiResponse),
            ];
        }
        return [{}, {}];
    }, [cartResponse, data?.cartSource]);

    const onSavingsLineItemLoad = useCallback(
        (title?: string, subtitle?: string, isPaymentCouponWidget?: boolean): void => {
            const lineItemData = getSavingsCornerClickStreamData(title, subtitle, isPaymentCouponWidget);
            impressionSavingsCornerLineItem(
                impressionEvent,
                cartId,
                { ...lineItemData, ...analyticsContext },
                analyticsExtraParams,
            );
        },
        [cartId, impressionEvent, analyticsExtraParams, analyticsContext],
    );

    const { getDirectCouponSavingsItem } = useDirectCouponSavingsData(
        applyCouponWidgetData,
        applyPaymentCouponWidgetData,
        onSavingsLineItemLoad,
        isNewPORFlow,
    );
    const { getAppliedCouponSavingsItems } = useAppliedCouponSavingsData(
        applyCouponWidgetData,
        applyPaymentCouponWidgetData,
        onSavingsLineItemLoad,
        isNewPORFlow,
    );
    const { getCouponLessSavingsItem } = useCouponlessSavingsData(onSavingsLineItemLoad);
    const { getCouponButtonSavingsItem } = useCouponButtonSavingsData(
        applyCouponWidgetData,
        applyPaymentCouponWidgetData,
        onSavingsLineItemLoad,
    );

    const { getIMCouponLessSavingsItem } = useIMCouponlessSavingsData(onSavingsLineItemLoad);

    const getCouponSavingsData = useCallback(
        (renderingItemData: RenderingDetails) => {
            switch (renderingItemData.type.toUpperCase()) {
                case VALID_RENDERING_DETAILS_TYPE.COUPON_BUTTON: {
                    const isDirectCouponScenario = isDirectCouponUsecase(
                        applyCouponWidgetData,
                        xpSelectiveCouponVariant,
                        valueDrivenCrossSellXPVariant,
                    );

                    if (isDirectCouponScenario) {
                        return getDirectCouponSavingsItem(
                            valueDrivenCrossSellXPVariant === ValueDrivenCrossSellXP.COUPON_LOCKED_WIDGET,
                            renderingItemData,
                        );
                    }
                    return getCouponButtonSavingsItem(renderingItemData);
                }

                case VALID_RENDERING_DETAILS_TYPE.PAYMENT_COUPON_BUTTON: {
                    if (!isNewPORFlow) {
                        return [];
                    }
                    const isDirectPaymentCouponScenario = isDirectPaymentCouponUsecase(applyPaymentCouponWidgetData);
                    if (isDirectPaymentCouponScenario) {
                        return getDirectCouponSavingsItem(false, renderingItemData, CouponType.PAYMENT);
                    }
                    return getCouponButtonSavingsItem(renderingItemData);
                }

                case VALID_RENDERING_DETAILS_TYPE.PAYMENT_COUPON_REMOVE_BUTTON:
                    if (!isNewPORFlow) {
                        return [];
                    }
                    return getAppliedCouponSavingsItems(renderingItemData.icon, CouponType.PAYMENT);

                case VALID_RENDERING_DETAILS_TYPE.COUPON_REMOVE_BUTTON:
                    return getAppliedCouponSavingsItems(renderingItemData.icon);

                case VALID_RENDERING_DETAILS_TYPE.COUPONLESS_SAVINGS:
                    return getCouponLessSavingsItem(renderingItemData.icon, renderingItemData.meta);

                case VALID_RENDERING_DETAILS_TYPE.IM_COUPONLESS_SAVINGS:
                    return getIMCouponLessSavingsItem(renderingItemData.icon);
            }
        },
        [
            applyCouponWidgetData,
            applyPaymentCouponWidgetData,
            getAppliedCouponSavingsItems,
            getCouponButtonSavingsItem,
            getCouponLessSavingsItem,
            getDirectCouponSavingsItem,
            isNewPORFlow,
            getIMCouponLessSavingsItem,
            valueDrivenCrossSellXPVariant,
            xpSelectiveCouponVariant,
        ],
    );

    const getCouponSavingsLineItems = useCallback(() => {
        return couponSavingsRenderingDetails.reduce<SavingsLineItemData[]>((acc, renderingDetail) => {
            const lineItemData = getCouponSavingsData(renderingDetail);

            if (!isEmpty(lineItemData)) {
                Array.isArray(lineItemData) ? acc.push(...lineItemData) : acc.push(lineItemData);
            }
            return acc;
        }, []);
    }, [couponSavingsRenderingDetails, getCouponSavingsData]);

    return { getCouponSavingsLineItems };
};
